import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { EM_DASH } from '~/common/constants/unicode';
import { Actions } from '~/eds/src/components/Actions/Actions';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { Chip } from '~/eds/src/components/Chip/Chip';
import { Chips } from '~/eds/src/components/Chips/Chips';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Mark } from '~/eds/src/components/Mark/Mark';
import { Text } from '~/eds/src/components/Text/Text';
import { Nullable } from '~/eds/src/types/Nullable';
import { scrollElementIntoView } from '~/eds/src/utils/scrollElementIntoView';
import { DataFieldType } from '~/enums/DataFieldType';
import { FieldEdit } from '~/features/document-bulk-actions/EditFields/FieldEdit';
import {
  Field as FieldType,
  FieldValue,
} from '~/features/document-handler/types/fields';
import { getFieldDisplayValue } from '~/features/document-handler/utils';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { api } from '~/redux/api/createApi';
import { PilotId } from '~/types/PilotId';

type Props = {
  enableFieldEditing?: boolean;
  isActive?: boolean;
  field: FieldType;
  documentId: PilotId;
  sectionEditMode?: boolean;
  onValueChange?: (id: number, label: string, value: any) => void;
};

export const Field = ({
  field,
  documentId,
  isActive = false,
  enableFieldEditing = true,
  sectionEditMode = false,
  onValueChange,
}: Props) => {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const formatNumber = useFormatNumber();
  const isEditFieldEnabled = useFlag(FlagType.EditFieldInDocumentDetailsPanel);
  const isAskAnythingV3Enabled = useFlag(FlagType.AskAnythingV3);

  const hasDocumentDetailsPanelUpdate = useFlag(
    FlagType.DocumentDetailsPanelUpdate,
  );

  const initialValue = Array.isArray(field.value)
    ? field.value.map((el) => el.value)
    : field.type === DataFieldType.DATE
      ? formatDate(field.value?.value, 'iso')
      : (field.value?.value ?? null);

  const initialDisplayValue = field.displayValue;

  const isArrayField =
    field.type === DataFieldType.ARRAY_MULTIPLE ||
    field.type === DataFieldType.ATTACHMENT;

  const [isEditing, setIsEditing] = useState(false);
  const [fieldValue, setFieldValue] =
    useState<Nullable<string | number | boolean | string[] | Date | null>>(
      initialValue,
    );
  const [displayValue, setDisplayValue] = useState(initialDisplayValue);
  const [arrayDisplayValues, setArrayDisplayValues] = useState<string[]>(
    isArrayField && Array.isArray(field.value)
      ? (field.value as FieldValue[]).map((v) => v.displayValue).filter(Boolean)
      : [],
  );

  const { toast } = useToast();
  const [updateDocument, { isLoading: isUpdateDocumentLoading }] =
    api.endpoints.updateDocument.useMutation();

  const handleToggleEdit = () => {
    setIsEditing(!isEditing);
  };

  useEffect(() => {
    if (!sectionEditMode) {
      setFieldValue(initialValue);
      setDisplayValue(initialDisplayValue);
    }
  }, [sectionEditMode]);

  useEffect(() => {
    if (isActive) {
      scrollElementIntoView(`field-${field.id}`, {
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [isActive]);

  const formatFieldValue = (field: FieldType, value: any) => {
    if (field.type === DataFieldType.DATE) {
      return formatDate(value, 'iso');
    }

    return value;
  };

  const handleSave = async () => {
    try {
      const updatedFields = await updateDocument({
        documentId,
        fields: [
          {
            id: field.id,
            label: field.name,
            value: formatFieldValue(field, fieldValue),
          },
        ],
      }).unwrap();
      const editedField = updatedFields?.find((f) => f.id === field.id);
      if (editedField) {
        setDisplayValue(
          getFieldDisplayValue(
            field.type,
            editedField.value,
            formatDate,
            formatNumber,
          ),
        );
        if (isArrayField && Array.isArray(editedField.value)) {
          setArrayDisplayValues(
            (editedField.value as FieldValue[])
              .map((v) => v.displayValue)
              .filter(Boolean),
          );
        }
      }
      toast({
        message: intl.formatMessage({
          id: 'Field.fieldUpdatedSuccessfully',
          defaultMessage: 'Field updated successfully',
          description: 'Success message when a field is updated successfully',
        }),
        status: 'success',
      });
    } catch {
      toast({
        message: intl.formatMessage({
          id: 'Field.fieldUpdateError',
          defaultMessage: 'An error occurred while updating the field',
          description: 'Error message when field update fails',
        }),
        status: 'danger',
      });
    } finally {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setFieldValue(initialValue);
    setDisplayValue(initialDisplayValue);
    setIsEditing(false);
  };
  const isZeroNumberValue =
    field.type === DataFieldType.NUMBER &&
    fieldValue !== null &&
    fieldValue !== '' &&
    Number(fieldValue) === 0;

  const emptyText = intl.formatMessage({
    id: 'Field.emptyValue',
    defaultMessage: '(empty)',
    description: 'Displayed when a field has no value',
  });

  const chipStatus = !isActive || !isAskAnythingV3Enabled ? 'info' : 'ai';

  const renderValue = () => {
    if (!hasDocumentDetailsPanelUpdate) {
      return (
        <Text variant="body-medium" color="text.primary">
          {!isActive || !isAskAnythingV3Enabled ? (
            displayValue
          ) : (
            <Mark highlighter="ai"> {displayValue}</Mark>
          )}
        </Text>
      );
    }

    if (displayValue === EM_DASH && !isZeroNumberValue) {
      return (
        <Text variant="tiny" color="text.secondary">
          {emptyText}
        </Text>
      );
    }

    if (isArrayField) {
      if (arrayDisplayValues.length === 0) {
        return (
          <Text variant="tiny" color="text.secondary">
            {emptyText}
          </Text>
        );
      }
      return (
        <Chips
          chips={arrayDisplayValues.map((val) => ({
            status: chipStatus,
            text: val,
          }))}
        />
      );
    }

    return <Chip status={chipStatus} text={displayValue ?? ''} />;
  };

  if (sectionEditMode) {
    return (
      <Layout align="flex-start" styles={{ width: '100%' }}>
        <FieldEdit
          field={field as any}
          onChange={(newValue) => {
            setFieldValue(newValue);
            onValueChange?.(
              field.id,
              field.name,
              formatFieldValue(field, newValue),
            );
          }}
          value={fieldValue as any}
        />
      </Layout>
    );
  }

  if (!isEditing) {
    return (
      <Layout
        id={`field-${field.id}`}
        styles={componentStyles.fieldContainer}
        spacing={4}
        {...(hasDocumentDetailsPanelUpdate && { minW: 0 })}
      >
        <Box
          styles={{
            minWidth: 0,
            flex: 1,
            ...(hasDocumentDetailsPanelUpdate && { overflow: 'hidden' }),
          }}
        >
          {renderValue()}
        </Box>

        {isEditFieldEnabled &&
          !hasDocumentDetailsPanelUpdate &&
          field.isEditable &&
          enableFieldEditing && (
            <Box className="edit-button" styles={[componentStyles.hidden]}>
              <Button
                size="xs"
                icon="edit"
                mode="icon"
                text={intl.formatMessage({
                  id: 'Field.editFieldButton',
                  defaultMessage: 'Edit field',
                  description: 'Button text to edit a field',
                })}
                tooltip={intl.formatMessage({
                  id: 'Field.editFieldButton',
                  defaultMessage: 'Edit field',
                  description: 'Button text to edit a field',
                })}
                onClick={handleToggleEdit}
                PRIVATE_isPadded={false}
                variant="tertiary"
              />
            </Box>
          )}
      </Layout>
    );
  }
  return (
    <Layout direction="column" spacing={2}>
      <FieldEdit
        field={field as any}
        onChange={setFieldValue}
        value={fieldValue as any}
      />
      <Actions
        justify="end"
        actions={[
          {
            text: intl.formatMessage({
              id: 'Field.cancelButton',
              defaultMessage: 'Cancel',
              description: 'Cancel button text',
            }),
            onClick: handleCancel,
            icon: 'x',
            level: 'tertiary' as const,
            disabled: isUpdateDocumentLoading,
          },
          {
            text: intl.formatMessage({
              id: 'Field.saveButton',
              defaultMessage: 'Save',
              description: 'Save button text',
            }),
            onClick: handleSave,
            icon: 'check',
            level: 'action' as const,
            disabled: isUpdateDocumentLoading,
            isLoading: isUpdateDocumentLoading,
          },
        ]}
      />
    </Layout>
  );
};

const componentStyles = {
  fieldContainer: {
    '&:hover .edit-button': {
      visibility: 'visible',
    },
  },
  hidden: {
    visibility: 'hidden',
  },
};
